<template>
  <div class="contentWrapper" :style="interstitialLayout">
    <div v-if="baseData">
      <ThankyouLayout />
      <ads
        v-if="shouldShowAds"
        :key="`${campaignId}-${zoneId}-interstitial`"
        class="ads-area"
        :campaign-id="campaignId"
        :zone-id="zoneId"
        :position="'interstitial'"
        type="interstitial"
        :skip-enabled="skipEnabled"
        :skip-delay="skipDelay"
        @ad-loaded="onAdLoaded"
        @ad-started="onAdStarted"
        @ad-completed="onAdCompleted"
        @ad-skipped="onAdSkipped"
        @ad-error="onAdError"
      />
      <div class="postLogin-container">
        <ads-placeholder v-if="hasPreviewAds && hasInterstitialAd" />
        <div v-if="adTypeConfirmed && !isSambaAd && !isBrokenAd" class="redirect-area">
          <p v-if="!isManual" class="redirect-time">{{ timer }} s</p>
          <p v-if="isManual">
            <router-link class="next-btn" :to="{ name: 'thankyou' }"> → </router-link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Ads from '../components/ads/ads'
  import AdsPlaceholder from './ads-placeholder'
  import ThankyouLayout from './thankyouLayout.vue'
  import screens from '../utils/pageSchema'

  export default {
    name: 'InterstitialPage',
    components: {
      Ads,
      AdsPlaceholder,
      ThankyouLayout,
    },

    data() {
      return {
        timer: null,
        intervalId: null,
        timerStarted: false,
        isSambaAd: false,
        adTypeConfirmed: false,
        adStarted: false,
        isBrokenAd: false,
        adErrorCount: 0,
        adLoadedReceived: false,
        noAdTimeout: null,
        adsComponentInitialized: false,
      }
    },

    computed: {
      hasPreviewAds() {
        return !!(
          this.baseData?.flags?.isAdvertising &&
          this.baseData?.flags?.adsInterstitial &&
          !!this.baseData?.development?.preview
        )
      },
      baseData() {
        return this.$store.state.baseData
      },
      interstitialLayout() {
        return {
          color: this.baseData?.colors?.textColor,
          'font-family': this.baseData?.fonts?.font,
          'text-align': 'center',
        }
      },
      campaignId() {
        return this.$store.getters.postLogin?.id || null
      },
      redirectTime() {
        return this.baseData?.advertisement?.interstitial?.duration ?? 5
      },
      hasAdvertising() {
        return (
          (this.baseData?.flags?.isAdvertising && this.baseData?.flags?.adsInterstitial) ?? false
        )
      },
      interstitialData() {
        return this.baseData?.advertisement?.interstitial ?? {}
      },
      zoneId() {
        return this.baseData?.advertisement?.interstitial?.top?.id || null
      },
      hasInterstitialAd() {
        return !!(
          this.interstitialData &&
          this.interstitialData.top &&
          this.interstitialData.top.id &&
          this.hasAdvertising
        )
      },
      shouldShowAds() {
        return (
          (this.hasInterstitialAd && !this.hasPreviewAds && this.campaignId && this.zoneId) ||
          this.adsComponentInitialized
        )
      },
      isManual() {
        return !!(this.redirectTime === 0)
      },
      skipEnabled() {
        return this.interstitialData?.skipEnabled !== undefined
          ? this.interstitialData.skipEnabled === true
          : true
      },
      skipDelay() {
        return this.interstitialData?.skipDelay !== undefined ? this.interstitialData.skipDelay : 15
      },
    },

    mounted() {
      console.log('[INTERSTITIAL] Page mounted - AD CONDITIONS:', {
        hasInterstitialAd: this.hasInterstitialAd,
        hasPreviewAds: this.hasPreviewAds,
        campaignId: this.campaignId,
        zoneId: this.zoneId,
        hasAdvertising: this.hasAdvertising,
        baseData: !!this.baseData,
      })

      if (this.hasInterstitialAd && !this.hasPreviewAds && this.campaignId && this.zoneId) {
        this.adsComponentInitialized = true
      }
    },

    beforeDestroy() {
      if (this.intervalId) {
        clearInterval(this.intervalId)
      }
      if (this.noAdTimeout) {
        clearTimeout(this.noAdTimeout)
      }
    },
    methods: {
      async onAdLoaded(adData) {
        this.adsComponentInitialized = true

        console.log('[INTERSTITIAL] onAdLoaded', {
          adStarted: this.adStarted,
          isSambaAd: adData?.isSambaAd,
          isBrokenAd: adData?.isBrokenAd,
          adType: adData?.type,
          timerStarted: this.timerStarted,
          redirectTime: this.redirectTime,
          adErrorCount: this.adErrorCount,
        })

        // Mark that we received at least one successful ad load
        this.adLoadedReceived = true

        // Clear any pending timeout since we got an ad
        if (this.noAdTimeout) {
          clearTimeout(this.noAdTimeout)
          this.noAdTimeout = null
          console.log('[INTERSTITIAL] ✅ Ad loaded, canceling "no ads" timeout')
        }

        // Check if ad is marked as broken (should not happen for interstitial type now)
        if (adData?.isBrokenAd) {
          console.log('[INTERSTITIAL] 🚨 Broken ad detected, redirecting immediately without timer')
          this.isBrokenAd = true
          this.adTypeConfirmed = true
          this.isSambaAd = false
        }

        // Don't start timer for broken ads (navigation handled by broken ad UI countdown)
        if (this.isBrokenAd) {
          sessionStorage.setItem('interstitialShown', 'true')
          await screens.advance()
          return
        }

        const isSambaAd = adData?.isSambaAd || false
        console.log('[INTERSTITIAL] Ad type determined:', { isSambaAd, adType: adData?.type })

        if (!this.adTypeConfirmed) {
          this.adTypeConfirmed = true
          this.isSambaAd = isSambaAd
        }

        if (isSambaAd) {
          console.log('[INTERSTITIAL] Samba video ad loaded, waiting for ad-started event')
          return
        }

        if (!this.timerStarted) {
          console.log('[INTERSTITIAL] Non-Samba ad (static content) detected, starting timer')
          this.timerStarted = true
          this.timer = this.redirectTime
          if (!this.isManual) {
            this.intervalId = setInterval(async () => {
              if (this.timer > 0) {
                this.timer = this.timer - 1
              } else {
                clearInterval(this.intervalId)
                sessionStorage.setItem('interstitialShown', 'true')
                await screens.advance()
              }
            }, 1000)
          }
        }
      },
      onAdStarted(adData) {
        console.log('[INTERSTITIAL] onAdStarted (Samba video ad started playing)', {
          isSambaAd: adData?.isSambaAd,
          timerStarted: this.timerStarted,
          redirectTime: this.redirectTime,
        })

        this.adStarted = true
        this.isBrokenAd = false

        this.adTypeConfirmed = true
        this.isSambaAd = adData?.isSambaAd || false

        if (!this.timerStarted) {
          console.log('[INTERSTITIAL] Starting timer for Samba video ad')
          this.timerStarted = true
          this.timer = this.redirectTime
          if (!this.isManual) {
            this.intervalId = setInterval(async () => {
              if (this.timer > 0) {
                this.timer = this.timer - 1
              } else {
                clearInterval(this.intervalId)
                sessionStorage.setItem('interstitialShown', 'true')
                await screens.advance()
              }
            }, 1000)
          }
        }
      },
      async onAdCompleted() {
        setTimeout(async () => {
          sessionStorage.setItem('interstitialShown', 'true')
          await screens.advance()
        }, 1000)
      },
      async onAdSkipped() {
        sessionStorage.setItem('interstitialShown', 'true')
        await screens.advance()
      },
      onAdError(errorData) {
        console.log('[INTERSTITIAL] onAdError - ad failed to load', {
          error: errorData?.error,
          zoneId: errorData?.zoneId,
          position: errorData?.position,
          adErrorCount: this.adErrorCount,
          adLoadedReceived: this.adLoadedReceived,
        })

        this.adErrorCount++

        if (this.adLoadedReceived) {
          // One ad failed but we already have content, continue gracefully
          console.log(
            '[INTERSTITIAL] ✅ Ad error but we already loaded an ad successfully, continuing',
          )
          return
        }

        if (!this.noAdTimeout && this.adErrorCount === 1) {
          // Wait 2 seconds to see if any other ads load
          console.log(
            '[INTERSTITIAL] ⏱️ No ads loaded yet, setting 2-second timeout to wait for other ads...',
          )
          this.noAdTimeout = setTimeout(() => {
            if (!this.adLoadedReceived) {
              // No ads loaded within timeout, redirect
              console.log('[INTERSTITIAL] ❌ No ads loaded within timeout, redirecting to thankyou')
              this.$router.push('thankyou')
            } else {
              console.log('[INTERSTITIAL] ✅ Ad loaded before timeout expired')
            }
          }, 2000)
        }
      },
    },
  }
</script>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Alegreya+Sans:wght@500&family=Archivo&family=B612&family=BioRhyme&family=Cairo&family=Cardo&family=Crimson+Text&family=Fjalla+One&family=Frank+Ruhl+Libre&family=IBM+Plex+Sans&family=Karla&family=Lora&family=Montserrat&family=Muli:wght@577&family=Playfair+Display&family=Roboto&family=Rubik&family=Source+Sans+Pro&family=Spectral&family=Titillium+Web&family=Varela&family=Vollkorn&family=Work+Sans&display=swap');

  .contentWrapper {
    position: relative;
    z-index: 1;
    width: 100vw;
    padding: 20px 0 0 0;
    background: transparent !important;
  }

  .postLogin-container {
    max-width: 600px;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 1;
    background: transparent !important;
  }

  .redirect-area {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
  }

  .redirect-time {
    font-size: 12px;
    background: #f4f4f4;
    display: inline-block;
    padding: 10px 20px;
    border-radius: 32px;
    font-weight: 600;
    color: #808080;
    margin-right: 10px;
  }

  .next-btn {
    background: #55acee;
    padding: 10px 20px;
    border-radius: 32px;
    color: white;
    text-decoration: none;
    font-size: 12px;
    cursor: pointer;
  }

  /* Permanent fix for thankyouLayout background visibility */
  ::v-deep .thankyou-layout-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 0;
    pointer-events: none;
  }

  .ads-area {
    width: 100%;
    max-width: 600px;
    margin: 0 auto 30px auto;
    overflow: hidden;
  }

  /* Ensure embedded ads don't break layout */
  .ads-area iframe {
    max-width: 100%;
    height: auto;
  }

  .ads-area .embedly-responsive {
    max-width: 100%;
    overflow: hidden;
  }
</style>
